import React, { Component, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { safeSetItem } from '@/lib/safeStorage';

interface Props {
  children: ReactNode;
  /** Shown in the fallback UI */
  label?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Generic error boundary.  Catches render/lifecycle errors in its subtree
 * and shows a recovery UI instead of crashing the whole app.
 *
 * Particularly important around Three.js / R3F canvases which can throw
 * WebGL context-lost errors that would otherwise white-screen the app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] error name:', error?.name);
    console.error('[ErrorBoundary] error message:', error?.message);
    console.error('[ErrorBoundary] stack:', error?.stack?.slice(0, 500));
    console.error('[ErrorBoundary] component stack:', info.componentStack?.slice(0, 500));
  }

  handleRetry = () => {
    // On crash recovery, restore from checkpoint so player respawns at a safe distance
    try {
      const raw = localStorage.getItem('aphenis_petworld_checkpoint');
      if (raw) {
        const cp = JSON.parse(raw);
        if (cp.pos && Array.isArray(cp.pos) && cp.pos.length >= 3) {
          // Offset position by 12 units in a random direction to avoid re-triggering combat
          const angle = Math.random() * Math.PI * 2;
          const safePos = [cp.pos[0] + Math.cos(angle) * 12, cp.pos[1], cp.pos[2] + Math.sin(angle) * 12];
          safeSetItem('aphenis_petworld_lastpos', JSON.stringify(safePos));
          if (cp.rot != null) safeSetItem('aphenis_petworld_lastrot', String(cp.rot));
        }
      }
    } catch {}
    this.setState({ hasError: false, error: null });
  };


  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[300] bg-background flex items-center justify-center p-6">
          <div className="text-center space-y-5 max-w-sm">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-foreground">
                {this.props.label || 'Something went wrong'}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {this.state.error?.message?.slice(0, 120) || 'An unexpected error occurred.'}
              </p>
            </div>
            <div className="flex justify-center">
              <Button onClick={this.handleRetry} className="rounded-full gap-2">
                <RotateCcw className="w-4 h-4" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
